import { HOLIDAYS } from '../constants/holidays';

/**
 * Hot Zone Detection Service
 *
 * Analyzes chart data to detect spikes, rank peak periods,
 * and generate upcoming holiday alerts with historical comparisons.
 */
class HotZoneService {
  /**
   * Detect days with unusually high order volume.
   * A spike is any day with orders >= threshold * average.
   *
   * @param {Array} chartData - Array of { date, ordersTotal, ... }
   * @param {number} threshold - Multiplier above average (default 2x)
   * @returns {Array} Array of { date, ordersTotal, multiplier, holidayLabel }
   */
  detectSpikes(chartData, threshold = 2) {
    if (!chartData || chartData.length < 3) return [];

    const total = chartData.reduce((sum, d) => sum + (d.ordersTotal || 0), 0);
    const avg = total / chartData.length;

    if (avg === 0) return [];

    const spikes = chartData
      .filter((d) => d.ordersTotal >= avg * threshold)
      .map((d) => ({
        date: d.date,
        ordersTotal: d.ordersTotal,
        multiplier: Math.round((d.ordersTotal / avg) * 10) / 10,
        holidayLabel: this._findHolidayForDate(d.date),
      }));

    return spikes;
  }

  /**
   * Group contiguous spike days into hot zones.
   *
   * @param {Array} spikes - Output of detectSpikes()
   * @returns {Array} Array of { startDate, endDate, totalOrders, label, days }
   */
  groupContiguousSpikes(spikes) {
    if (!spikes || spikes.length === 0) return [];

    const sorted = [...spikes].sort((a, b) => a.date.localeCompare(b.date));
    const zones = [];
    let currentZone = {
      startDate: sorted[0].date,
      endDate: sorted[0].date,
      totalOrders: sorted[0].ordersTotal,
      days: [sorted[0]],
    };

    for (let i = 1; i < sorted.length; i++) {
      const prevDate = new Date(sorted[i - 1].date + 'T00:00:00');
      const currDate = new Date(sorted[i].date + 'T00:00:00');
      const diffDays = (currDate - prevDate) / (1000 * 60 * 60 * 24);

      if (diffDays <= 2) {
        // Contiguous or 1-day gap — extend current zone
        currentZone.endDate = sorted[i].date;
        currentZone.totalOrders += sorted[i].ordersTotal;
        currentZone.days.push(sorted[i]);
      } else {
        // New zone
        currentZone.label = this._getLabelForZone(currentZone);
        zones.push(currentZone);
        currentZone = {
          startDate: sorted[i].date,
          endDate: sorted[i].date,
          totalOrders: sorted[i].ordersTotal,
          days: [sorted[i]],
        };
      }
    }

    currentZone.label = this._getLabelForZone(currentZone);
    zones.push(currentZone);

    return zones;
  }

  /**
   * Get ranked peak periods from chart data.
   * Returns top N periods by total orders.
   *
   * @param {Array} chartData - Full chart data array
   * @param {number} limit - Max periods to return (default 10)
   * @returns {Array} Ranked array of { startDate, endDate, totalOrders, label, avgMultiplier }
   */
  getRankedPeakPeriods(chartData, limit = 10) {
    const spikes = this.detectSpikes(chartData, 1.5);
    const zones = this.groupContiguousSpikes(spikes);

    // Calculate average multiplier for each zone
    const ranked = zones.map((zone) => {
      const avgMultiplier =
        zone.days.reduce((sum, d) => sum + d.multiplier, 0) / zone.days.length;
      return {
        ...zone,
        avgMultiplier: Math.round(avgMultiplier * 10) / 10,
      };
    });

    // Sort by total orders descending
    ranked.sort((a, b) => b.totalOrders - a.totalOrders);

    return ranked.slice(0, limit);
  }

  /**
   * Get upcoming holiday alerts within the next N days.
   * Each alert includes the holiday name, days until, and last year's order count.
   *
   * @param {Date} today - Reference date
   * @param {Array} lastYearChartData - Chart data from the previous year (optional)
   * @param {number} lookAheadDays - Days to look ahead (default 14)
   * @returns {Array} Array of { holiday, daysUntil, lastYearOrders, message }
   */
  getUpcomingHolidayAlerts(today, lastYearChartData = [], lookAheadDays = 14) {
    const alerts = [];
    const year = today.getFullYear();

    // Check this year's holidays and next year's early ones (for Dec→Jan)
    const yearsToCheck = [year, year + 1];

    for (const checkYear of yearsToCheck) {
      for (const holiday of HOLIDAYS) {
        const range = holiday.getDateRange(checkYear);
        const startDate = new Date(range.start + 'T00:00:00');
        const daysUntil = Math.ceil(
          (startDate - today) / (1000 * 60 * 60 * 24)
        );

        if (daysUntil >= 0 && daysUntil <= lookAheadDays) {
          // Find last year's data for this holiday
          const lastYearRange = holiday.getDateRange(checkYear - 1);
          const lastYearOrders = this._sumOrdersInRange(
            lastYearChartData,
            lastYearRange.start,
            lastYearRange.end
          );

          const daysLabel =
            daysUntil === 0
              ? 'hoy'
              : daysUntil === 1
                ? 'manana'
                : `en ${daysUntil} dias`;

          let message = `${holiday.name} ${daysLabel}`;
          if (lastYearOrders > 0) {
            message += ` — el ano pasado: ${lastYearOrders} pedidos de ${holiday.keyProduct.toLowerCase()}`;
          }

          alerts.push({
            holiday: holiday.name,
            holidayId: holiday.id,
            keyProduct: holiday.keyProduct,
            daysUntil,
            startDate: range.start,
            endDate: range.end,
            lastYearOrders,
            message,
          });
        }
      }
    }

    // Sort by days until (soonest first)
    alerts.sort((a, b) => a.daysUntil - b.daysUntil);

    return alerts;
  }

  /**
   * Check if a specific date falls within any known holiday.
   */
  _findHolidayForDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const year = date.getFullYear();

    for (const holiday of HOLIDAYS) {
      const range = holiday.getDateRange(year);
      if (dateStr >= range.start && dateStr <= range.end) {
        return holiday.name;
      }
    }
    return null;
  }

  /**
   * Get a label for a hot zone (check if it matches a known holiday).
   */
  _getLabelForZone(zone) {
    // Check if any day in the zone has a holiday label
    for (const day of zone.days) {
      if (day.holidayLabel) return day.holidayLabel;
    }
    // Fallback: use date range
    if (zone.startDate === zone.endDate) {
      return this._formatDisplayDate(zone.startDate);
    }
    return `${this._formatDisplayDate(zone.startDate)} - ${this._formatDisplayDate(zone.endDate)}`;
  }

  /**
   * Sum ordersTotal in chart data for a given date range.
   */
  _sumOrdersInRange(chartData, startDate, endDate) {
    return chartData
      .filter((d) => d.date >= startDate && d.date <= endDate)
      .reduce((sum, d) => sum + (d.ordersTotal || 0), 0);
  }

  _formatDate(date) {
    return date.toISOString().split('T')[0];
  }

  _formatDisplayDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  }
}

const hotZoneService = new HotZoneService();
export default hotZoneService;
